/* ══════════════════════════════════════════════════════════════════════════
   Google Analytics 4 (gtag.js)
   ──────────────────────────────────────────────────────────────────────────
   PASO ÚNICO PARA ACTIVAR: reemplaza el placeholder de abajo por el ID de
   medición de tu propiedad GA4 (formato G-XXXXXXXXXX). Mientras contenga el
   placeholder, el tracking queda desactivado y no afecta al sitio.

   Eventos de conversión que emite el sitio (márcalos como "eventos clave" en
   GA4 → Administrar → Eventos):
     • whatsapp_click  — clic en cualquier botón/enlace de WhatsApp
     • phone_click     — clic en el número de teléfono (enlace tel:)
     • generate_lead   — envío exitoso del formulario "Agendar consulta"

   En Google Ads cada uno va a su propia acción de conversión y se cuenta una
   vez por sesión; ver el bloque "Acciones de conversión" más abajo.
   ══════════════════════════════════════════════════════════════════════════ */

export const GA_MEASUREMENT_ID = 'G-23LBY7BTVT';

// Google Ads: ID de conversión + etiqueta de la acción de conversión "Contacto"
// (formulario / WhatsApp / llamada). Cargar el tag AW aquí evita el problema de
// "la etiqueta no se ha instalado" del asistente de Google Ads.
export const GADS_ID = 'AW-18356333375';

/* ── Acciones de conversión ────────────────────────────────────────────────
   Hasta ahora las tres formas de contacto —clic en WhatsApp, clic en el
   teléfono y envío del formulario— enviaban la MISMA conversión. En Ads las
   tres llegaban como "Contacto", así que era imposible saber qué campaña
   traía formularios y cuál sólo traía clics de WhatsApp. Y como abrir
   WhatsApp es lo más frecuente y lo de menor intención (abrir el chat no es
   escribir), la puja automática optimizaba hacia la señal más barata.

   Ahora cada una tiene su acción. PARA ACTIVARLO: crear en Google Ads
   (Objetivos → Conversiones → Acción nueva → Sitio web → añadir manualmente)
   dos acciones más y pegar aquí sus etiquetas —la parte que va después de la
   barra en el send_to—:

     • Formulario → dejar la etiqueta actual, que ya es la del formulario
     • WhatsApp   → crear y pegar en ETIQUETA_WHATSAPP
     • Llamada    → crear y pegar en ETIQUETA_LLAMADA

   Y en Ads marcar Formulario y Llamada como acción PRINCIPAL, y WhatsApp
   como SECUNDARIA: así el algoritmo puja por los contactos que se pueden
   verificar y sigue viendo los de WhatsApp sin optimizar hacia ellos.

   Mientras una etiqueta esté vacía, esa señal sigue enviándose a la acción
   original, de modo que nunca se deja de medir. */
const ETIQUETA_FORMULARIO = 'ZBXcCL36ndgcEL_W_bBE';
const ETIQUETA_WHATSAPP = '';
const ETIQUETA_LLAMADA = '';

export type TipoContacto = 'formulario' | 'whatsapp' | 'llamada';

const ETIQUETAS: Record<TipoContacto, string> = {
  formulario: ETIQUETA_FORMULARIO,
  whatsapp: ETIQUETA_WHATSAPP || ETIQUETA_FORMULARIO,
  llamada: ETIQUETA_LLAMADA || ETIQUETA_FORMULARIO,
};

/* Valor relativo de cada contacto. Un formulario trae nombre, teléfono y el
   relato del caso; una llamada trae a alguien dispuesto a hablar ahora; un
   clic en WhatsApp trae, muchas veces, sólo un clic. Los valores ordenan esa
   diferencia para el día en que se puje por valor de conversión. */
const VALORES: Record<TipoContacto, number> = {
  formulario: 3,
  llamada: 2,
  whatsapp: 1,
};

const isEnabled = (): boolean =>
  /^G-[A-Z0-9]{6,}$/.test(GA_MEASUREMENT_ID) && !GA_MEASUREMENT_ID.includes('XXXXX');

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

let initialized = false;

/** Carga gtag.js y configura la propiedad. Llamar una sola vez al iniciar. */
export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined' || !isEnabled()) return;
  initialized = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // send_page_view:false → controlamos page_view manualmente (SPA con React Router)
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  // Etiqueta de Google Ads (para medir conversiones sin depender del asistente)
  window.gtag('config', GADS_ID);

  setupAutoClickTracking();
}

/* Una conversión por tipo y por sesión. Antes cada clic disparaba una: quien
   pulsaba WhatsApp, volvía a la pestaña y lo pulsaba otra vez contaba dos
   veces, y el coste por conversión que muestra Ads salía más barato de lo
   real. La sesión es la unidad correcta —una persona que contacta es un
   contacto, la pulse una o cinco veces— y deja pasar a quien vuelve otro día,
   que sí es un contacto nuevo. */
function yaContado(tipo: TipoContacto): boolean {
  const clave = `sv_conv_${tipo}`;
  try {
    if (sessionStorage.getItem(clave)) return true;
    sessionStorage.setItem(clave, '1');
    return false;
  } catch {
    // Modo privado: sin memoria de sesión, mejor contar que no medir.
    return false;
  }
}

/** Registra en Google Ads un contacto del tipo indicado, una vez por sesión. */
export function adsLeadConversion(tipo: TipoContacto = 'formulario'): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.gtag) return;
  if (yaContado(tipo)) return;
  window.gtag('event', 'conversion', {
    send_to: `${GADS_ID}/${ETIQUETAS[tipo]}`,
    value: VALORES[tipo],
    currency: 'CLP',
  });
}

/** Envía un page_view. Se llama en cada cambio de ruta de la SPA. */
export function trackPageView(path: string): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** Envía un evento personalizado (p. ej. conversiones). */
export function gaEvent(name: string, params: Record<string, any> = {}): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', name, params);
}

/* Rastreo automático de clics en WhatsApp y teléfono, por delegación de eventos:
   captura clics en cualquier <a> con href de WhatsApp o tel:, presente o futuro,
   sin tener que anotar cada botón individualmente. */
function setupAutoClickTracking(): void {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const a = target?.closest?.('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (/wa\.me|api\.whatsapp\.com|whatsapp:/i.test(href)) {
      gaEvent('whatsapp_click', {
        link_url: href,
        link_text: (a.textContent || '').trim().slice(0, 80) || 'WhatsApp',
      });
      adsLeadConversion('whatsapp');
    } else if (/^tel:/i.test(href)) {
      gaEvent('phone_click', { link_url: href });
      adsLeadConversion('llamada');
    }
  }, { capture: true });
}
